import { useState } from "react";
import { Link } from "react-router-dom";
import { updateMyAffiliatePayoutDetails } from "wasp/client/operations";
import { Button } from "../../client/components/ui/button";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { Input } from "../../client/components/ui/input";
import { Label } from "../../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";

interface PayoutSettingsCardProps {
  profile: {
    payoutMethod: string | null;
    payoutDetails: string | null;
  };
  isDark: boolean;
}

export default function PayoutSettingsCard({
  profile,
  isDark,
}: PayoutSettingsCardProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const [payoutMethod, setPayoutMethod] = useState(
    profile.payoutMethod ?? "iban",
  );
  const [payoutDetails, setPayoutDetails] = useState(
    profile.payoutDetails ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateMyAffiliatePayoutDetails({
        payoutMethod: payoutMethod as "iban" | "paypal",
        payoutDetails: payoutDetails.trim(),
      });
      toast({ description: t.affiliate.settings.saved });
    } catch (err: any) {
      toast({
        variant: "destructive",
        description: err?.message || t.affiliate.settings.saveError,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.affiliate.settings.title}
        isDark={isDark}
        compact
      />
      <CardContent className="px-4 pb-4 pt-3 flex-1 flex flex-col gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="affiliate-payout-method">
            {t.affiliate.settings.method}
          </Label>
          <Select value={payoutMethod} onValueChange={setPayoutMethod}>
            <SelectTrigger id="affiliate-payout-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="iban">
                {t.affiliate.settings.methodIban}
              </SelectItem>
              <SelectItem value="paypal">
                {t.affiliate.settings.methodPaypal}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="affiliate-payout-details">
            {payoutMethod === "iban"
              ? t.affiliate.settings.detailsIban
              : t.affiliate.settings.detailsPaypal}
          </Label>
          <Input
            id="affiliate-payout-details"
            value={payoutDetails}
            onChange={(e) => setPayoutDetails(e.target.value)}
            className="font-mono"
          />
        </div>

        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving || payoutDetails.trim().length < 5}
        >
          {t.affiliate.settings.save}
        </Button>

        <p className="text-xs text-muted-foreground">
          <Link to="/account" className="underline hover:text-foreground">
            {t.affiliate.settings.changePassword}
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
